import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MyAttendance = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAttendance();
  }, [month, year]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('employeeToken');
      const res = await fetch(`/api/employee/attendance?month=${month}&year=${year}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to fetch attendance data');
      }
    } catch (err) {
      setError(err.message || 'Error fetching attendance');
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const daysList = data?.days_in_month ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : [];

  return (
    <div className="space-y-6">
      {/* Month & Stats Banner */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            My Attendance — {monthNames[month - 1]} {year}
          </h2>
          <p className="text-xs text-muted-foreground">Read-only monthly punch register</p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-xs font-medium focus:ring-1 focus:ring-teal-500"
          >
            {monthNames.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>{name}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-xs font-medium focus:ring-1 focus:ring-teal-500"
          >
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border p-4 rounded-xl shadow-sm">
            <p className="text-xs font-medium text-muted-foreground mb-1">Present Days</p>
            <p className="text-2xl font-bold text-emerald-600">{data.summary.present_days}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-xl shadow-sm">
            <p className="text-xs font-medium text-muted-foreground mb-1">Absent Days</p>
            <p className="text-2xl font-bold text-rose-600">{data.summary.absent_days}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-xl shadow-sm">
            <p className="text-xs font-medium text-muted-foreground mb-1">Leaves / Holidays</p>
            <p className="text-2xl font-bold text-amber-600">{data.summary.leave_days}</p>
          </div>
          <div className="bg-card border border-border p-4 rounded-xl shadow-sm">
            <p className="text-xs font-medium text-muted-foreground mb-1">Total Work Hours</p>
            <p className="text-2xl font-bold text-teal-600">{data.summary.total_hours} hrs</p>
          </div>
        </div>
      )}

      {/* Monthly Attendance Grid */}
      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
          <Icon name="Loader2" size={28} className="animate-spin text-teal-600 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading attendance register...</p>
        </div>
      ) : error ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-rose-500 font-semibold shadow-sm">
          {error}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-bold text-foreground">Monthly Day Grid</h3>
            <p className="text-[11px] text-muted-foreground">Scroll horizontally to view all days</p>
          </div>

          <div className="overflow-x-auto p-4">
            <div className="flex gap-1.5 min-w-max pb-2">
              {daysList.map(d => {
                const dateStr = sprintf('%04d-%02d-%02d', year, month, d);
                const w = new Date(year, month - 1, d).getDay();
                const isSunday = (w === 0);
                const cellData = data?.cells?.[d] || data?.cells?.[String(d)];
                const status = cellData?.status;
                const hours = cellData?.hours;

                let badge = null;
                let bgClass = 'bg-muted/10 border-border/40';

                if (status === 'present') {
                  badge = <span className="text-emerald-600 font-bold text-xs">{hours ? Number(hours).toFixed(1) : 'P'}</span>;
                  bgClass = 'bg-emerald-500/10 border-emerald-500/20';
                } else if (status === 'late') {
                  badge = <span className="text-emerald-600 font-bold text-xs">{hours ? Number(hours).toFixed(1) : 'L'}</span>;
                  bgClass = 'bg-emerald-500/10 border-emerald-500/20';
                } else if (status === 'half_day') {
                  badge = <span className="text-blue-600 font-bold text-[10px]">HD</span>;
                  bgClass = 'bg-blue-500/10 border-blue-500/20';
                } else if (status === 'paid_leave') {
                  badge = <span className="text-purple-600 font-bold text-[10px]">PL</span>;
                  bgClass = 'bg-purple-500/10 border-purple-500/20';
                } else if (status === 'leave') {
                  badge = <span className="text-amber-600 font-bold text-[10px]">L</span>;
                  bgClass = 'bg-amber-500/10 border-amber-500/20';
                } else if (status === 'week_off') {
                  badge = <span className="text-gray-500 font-bold text-[10px]">WO</span>;
                  bgClass = 'bg-gray-500/10 border-gray-500/20';
                } else if (status === 'sunday' || (isSunday && !cellData)) {
                  badge = <span className="text-gray-400 font-normal text-[10px]">Sun</span>;
                  bgClass = 'bg-gray-500/5 border-gray-500/10';
                } else if (status === 'absent') {
                  badge = <span className="text-rose-600 font-bold text-[10px]">A</span>;
                  bgClass = 'bg-rose-500/10 border-rose-500/20';
                }

                return (
                  <div key={d} className={`w-12 h-16 border rounded-lg flex flex-col items-center justify-between p-1.5 ${bgClass}`}>
                    <span className={`text-[10px] font-bold ${isSunday ? 'text-rose-500' : 'text-muted-foreground'}`}>{d}</span>
                    <div className="flex items-center justify-center flex-1">{badge}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function sprintf(format, ...args) {
    let i = 0;
    return format.replace(/%02d|%04d/g, match => {
      const val = String(args[i++]);
      const len = match === '%04d' ? 4 : 2;
      return val.padStart(len, '0');
    });
  }
};

export default MyAttendance;
